/**
 * @file useDocumentActions.ts
 * @description Действия карточки документа (phase19.31, часть 3): выпуск,
 * отметка о подписи, PDF (скачать, печать, открыть, создать заново),
 * аннулирование с причиной и удаление черновика.
 *
 * Подтверждения — окнами кабинета, не `window.confirm/prompt`: обычные —
 * общим окном подтверждения (`useConfirm`), аннулирование — окном с
 * обязательной причиной (`ReasonDialog`, открывает страница по `voiding`).
 * Ошибка сервера — всплывающим сообщением с его человеческим текстом.
 * Печать не удалась в странице — сообщение с кнопкой «Открыть PDF в новой
 * вкладке» (вкладка открывается по нажатию человека, не сама).
 */

import { useState } from "react";

import { openBlob, printBlob } from "./document-pdf";
import type { DocActionKey } from "./document-actions";
import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm, type ConfirmApi } from "../../ui/use-confirm";
import { ApiError } from "../../../lib/api-client";
import {
  deleteDocument,
  downloadDocumentPdf,
  issueDocument,
  regenerateDocumentPdf,
  signDocument,
  voidDocument,
  type DocumentDto,
} from "../../../lib/api-crm";
import { saveBlob } from "../../../lib/download";

/** Что ещё идёт: действие шапки, создание PDF или открытие файла. */
export type DocumentBusy = DocActionKey | "regenerate" | "open" | null;

export interface DocumentActions {
  busy: DocumentBusy;
  run: (key: DocActionKey) => void;
  /** Окно аннулирования открыто. */
  voiding: boolean;
  closeVoid: () => void;
  confirmVoid: (reason: string) => Promise<boolean>;
  regenerate: () => void;
  openPdf: () => void;
  confirm: ConfirmApi;
}

interface Options {
  doc: DocumentDto;
  replace: (doc: DocumentDto) => void;
  /** Черновик удалён — уйти к реестру. */
  onDeleted: () => void;
}

export function useDocumentActions({ doc, replace, onDeleted }: Options): DocumentActions {
  const notify = useNotify();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<DocumentBusy>(null);
  const [voiding, setVoiding] = useState(false);

  /** Выполнить запрос с занятостью и сообщением об ошибке; `true` — получилось. */
  async function attempt(key: Exclude<DocumentBusy, null>, fallback: string, work: () => Promise<void>): Promise<boolean> {
    setBusy(key);
    try {
      await work();
      return true;
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : fallback);
      return false;
    } finally {
      setBusy(null);
    }
  }

  const pdf = () => downloadDocumentPdf(doc.id, doc.number);

  const handlers: Record<DocActionKey, () => Promise<unknown>> = {
    issue: async () => {
      const ok = await confirm.confirm({
        title: "Выпустить документ?",
        description: "Документу будет присвоен номер и создан PDF. После выпуска изменить документ нельзя — только аннулировать.",
        action: "Выпустить",
      });
      if (!ok) return;
      await attempt("issue", "Не удалось выпустить документ", async () => {
        const updated = await issueDocument(doc.id);
        replace(updated);
        notify.success(`Документ выпущен: ${updated.number}`);
      });
    },
    sign: async () => {
      const ok = await confirm.confirm({
        title: "Отметить документ подписанным?",
        description: "Отметьте, когда на руках скан документа с живой подписью. Снять отметку нельзя.",
        action: "Отметить подписанным",
      });
      if (!ok) return;
      await attempt("sign", "Не удалось отметить подпись", async () => {
        replace(await signDocument(doc.id, "LIVE_SIGNATURE_SCAN"));
        notify.success("Документ отмечен подписанным");
      });
    },
    download: () => attempt("download", "Не удалось скачать PDF", async () => {
      const file = await pdf();
      saveBlob(file.blob, file.filename);
    }),
    print: () => attempt("print", "Не удалось подготовить PDF к печати", async () => {
      const file = await pdf();
      // Браузер не печатает из страницы — вкладку открывает человек кнопкой:
      // окно из `onload` рамки (не по нажатию) браузер заблокирует.
      printBlob(file.blob, () =>
        notify.warning("Браузер не дал напечатать прямо из кабинета", {
          action: {
            label: "Открыть PDF в новой вкладке",
            onClick: () => {
              if (!openBlob(file.blob)) saveBlob(file.blob, file.filename);
            },
          },
        }),
      );
    }),
    void: async () => setVoiding(true),
    delete: async () => {
      const ok = await confirm.confirm({
        title: "Удалить черновик?",
        description: "Черновик без номера будет удалён полностью. Нумерация документов не пострадает.",
        action: "Удалить черновик",
        danger: true,
      });
      if (!ok) return;
      const done = await attempt("delete", "Не удалось удалить черновик", () => deleteDocument(doc.id));
      if (done) {
        notify.success("Черновик удалён");
        onDeleted();
      }
    },
  };

  return {
    busy,
    run: (key) => void handlers[key](),
    voiding,
    closeVoid: () => setVoiding(false),
    confirmVoid: (reason) =>
      attempt("void", "Не удалось аннулировать документ", async () => {
        replace(await voidDocument(doc.id, reason));
        notify.success("Документ аннулирован");
      }),
    regenerate: () =>
      void attempt("regenerate", "Не удалось создать PDF", async () => {
        replace(await regenerateDocumentPdf(doc.id));
        notify.success("PDF создан заново");
      }),
    openPdf: () =>
      void attempt("open", "Не удалось открыть PDF", async () => {
        const file = await pdf();
        // Всплывающее окно заблокировано — файл просто скачивается.
        if (!openBlob(file.blob)) saveBlob(file.blob, file.filename);
      }),
    confirm,
  };
}
